/* eslint-disable react/no-array-index-key */
import React, { FunctionComponent, useRef, useState, useMemo } from 'react';

// MUI
import Box from '@mui/material/Box';
import { RichTreeView } from '@mui/x-tree-view';
import { TreeViewBaseItem } from '@mui/x-tree-view/models';

// OL
import { transform } from 'ol/proj';

// Custom components
import PPTreeItem, { PPTreeItemType } from '@/components/MapSidebarPanes/PP/PPTreeItem';
import PPInfoPaneSearch from '@/components/MapSidebarPanes/PP/PPInfoPaneSearch';
import usePPSearch from '@/components/usePPSearch';
import { escapeRegex } from '@/lib/helpers';

// Types
import { PPDocument, PPTextType } from '@/lib/prostorniPlanoviHelper';
import PPInfoPaneActions from './PPInfoPaneActions';

type IPPInfoPane = {
  // features: GFIFeatureType[];
  doc: PPDocument;
  planId: number;
  clickPosition: number[];
  planName: string;
};

const flatTree = (node: PPTextType): Array<PPTextType> => {
  if (Array.isArray(node.children) && node.children.length > 0) {
    return [node, ...node.children.map((x) => flatTree(x)).flat()];
  }
  return [node];
};

const PPInfoPane: FunctionComponent<IPPInfoPane> = (props) => {
  const { doc, planId, planName, clickPosition } = props;

  // const [root, setRoot] = useState<TreeViewBaseItem>({ id: 'root', label: 'root', children: [] });
  const defaultExpandedItemIds = doc
    .map((x) => flatTree(x).filter((node) => node.sql_model_name === undefined))
    .flat()
    .map((c) => c.id.toString());

  const [expandedIds, setExpandedIds] = useState<string[]>(defaultExpandedItemIds);

  const treeRef = useRef<HTMLDivElement>(null);

  const handleExpandItems = (itemIds: Array<string>) => {
    setExpandedIds((prev) => Array.from(new Set([...prev, ...itemIds])));
  };

  const { searchTerm, setSearchTerm, matches, goToMatch, currentMatchIndex } = usePPSearch({
    doc,
    treeRef,
    onExpandItems: handleExpandItems,
  });

  const generateTreeItemNode = (node: PPTextType): TreeViewBaseItem<PPTreeItemType> => {
    const text = `${node.numeration} ${node.value}`;

    return {
      itemId: node.id.toString(),
      id: node.id.toString(),
      label: text,
      isContent: true,
      isClanak: false,
    };
  };

  const generateTreeItemRecursively = (node: PPTextType): TreeViewBaseItem<PPTreeItemType> => {
    const isClanak = node.sql_model_name === 'OdClanak';
    const isContent = node.sql_model_name !== undefined && !isClanak;

    return {
      itemId: node.id.toString(),
      id: node.id.toString(),
      label: isClanak ? `Članak ${node.numeration}` : `${node.numeration} ${node.value}`,
      isClanak,
      isContent,
      // children: node.children?.map(x => generateTreeItem(x))
      children: isClanak
        ? node.children
            ?.map((x) => flatTree(x))
            .flat()
            .map((x) => generateTreeItemNode(x))
        : node.children?.map((x) => generateTreeItemRecursively(x)),
    };
  };

  const highlightText = (text: string, query: string, active = false) => {
    if (!query) return text;
    const regex = new RegExp(`(${escapeRegex(query)})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark
          key={i}
          style={{
            backgroundColor: active ? 'orange' : 'yellow',
          }}
        >
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const treeItems = useMemo(() => doc.map((x) => generateTreeItemRecursively(x)), [doc]);

  const parsedClickPosition = useMemo(
    () => transform(clickPosition, 'EPSG:3857', 'EPSG:3765').map((pos) => pos.toFixed(2)),
    [clickPosition]
  );

  return (
    <Box px={0.5} py={1} ref={treeRef} display="flex" flexDirection="column" overflow="hidden">
      <PPInfoPaneSearch
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        matches={matches}
        goToMatch={goToMatch}
        currentMatchIndex={currentMatchIndex}
      />

      <RichTreeView
        items={treeItems || []}
        expandedItems={expandedIds}
        onExpandedItemsChange={(event, ids) => setExpandedIds(ids ?? [])}
        itemChildrenIndentation={8}
        slots={{
          item: PPTreeItem,
        }}
        slotProps={{
          item: (item) => {
            const isActive = matches[currentMatchIndex]?.id === item.itemId;
            return {
              sx: { fontSize: '0.95rem', whiteSpace: 'pre-wrap' },
              label: highlightText(item.label as string, searchTerm, isActive),
              'data-id': item.itemId,
            };
          },
          root: {
            style: { overflowY: 'auto' },
          },
        }}
      />

      <PPInfoPaneActions planName={planName} clickPosition={parsedClickPosition} data={doc} planId={planId} />
    </Box>
  );
};

export default PPInfoPane;
